import { useTranslation } from 'react-i18next';
import { variableTranslations } from '../../constants/variableTranslations';
import { computeDescriptiveSummary, computeFiveNumberSummary } from '../../utils/statistics';

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '13px',
};

const cellStyle = {
  padding: '4px',
  borderBottom: '1px solid #eee',
  textAlign: 'left',
};

function getAttributeLabel(key, observations, translateLabel) {
  const directLabel = observations
    .filter((observation) => observation?.responseUnitKey === key)
    .map((observation) => observation?.label)
    .find((label) => typeof label === 'string' && label.trim() !== '');
  const rawLabel = observations
    .map((observation) => observation?.perceptualData?.[key]?.attributeName)
    .find((label) => typeof label === 'string' && label.trim() !== '') || directLabel || key;

  return variableTranslations[rawLabel]
    ? translateLabel(variableTranslations[rawLabel])
    : rawLabel;
}

function getFiniteValues(key, observations) {
  return observations
    .map((observation) => observation?.responseUnitKey === key
      ? observation.value
      : observation?.perceptualData?.[key]?.VASValue)
    .filter(Number.isFinite);
}

const displayOneDecimal = value => value.toFixed(1);
const displayUsefulPrecision = value => Number.isInteger(value) ? String(value) : value.toFixed(1);

function GovernedNumericSummary({
  observations,
  attributeKeys,
  minimumN,
  populationKind,
  summaryVariant,
  caption,
  summaryHelp,
  lowCountHelp,
  lowCountSingular,
  lowCountPlural,
  lowCountFormatter,
  externalPopulationLabel,
  showLowCountAttributeLabel,
  compactPopulationLowCount,
  populationLowCountMessage,
  hideAttributeColumn,
  feedbackT,
  translateLabel,
  attributeLabel,
  provenanceBreakdownByKey,
}) {
  const rows = attributeKeys
    .map((key) => {
      const values = getFiniteValues(key, observations);
      return {
        key,
        attribute: getAttributeLabel(key, observations, translateLabel),
        n: values.length,
        stats: values.length >= minimumN
          ? summaryVariant === 'compact-center-spread'
            ? computeDescriptiveSummary(values)
            : computeFiveNumberSummary(values)
          : null,
        provenanceBreakdown: provenanceBreakdownByKey?.[key] || null,
      };
    })
    .filter(({ n }) => n > 0);

  if (rows.length === 0) return null;

  const summaryRows = rows.filter(({ stats }) => stats);
  const insufficientRows = rows.filter(({ stats }) => !stats);
  const compact = summaryVariant === 'compact-center-spread';
  const isPvqdContext = ['pvqd-cape-v', 'pvqd-context'].includes(populationKind);
  const thresholdKey = populationKind === 'participants'
    ? 'feedback_participant_summary_threshold'
    : isPvqdContext
      ? 'feedback_pvqd_summary_threshold'
      : 'feedback_peer_summary_threshold';
  const thresholdDefault = isPvqdContext
    ? 'summary shown from {{minimumN}} rating observations'
    : 'summary shown from {{minimumN}} ratings';
  return (
    <div className="feedback-numeric-summary" data-population-kind={populationKind}>
      {summaryRows.length > 0 ? (
        <>
          <div className="catalog-detail__section-intro">
            {summaryHelp}
          </div>
          <table style={tableStyle}>
            <caption className={externalPopulationLabel ? 'feedback-visually-hidden' : undefined}>
              {caption}
            </caption>
            <thead>
              <tr>
                {(compact ? [
                  ...(hideAttributeColumn ? [] : [attributeLabel || feedbackT('attribute')]),
                  feedbackT('feedback_summary_n', { defaultValue: 'n' }),
                  feedbackT('feedback_summary_mean_sd', { defaultValue: 'Mean (SD)' }),
                  feedbackT('feedback_summary_median_iqr', { defaultValue: 'Median (Q1–Q3)' }),
                ] : [
                  attributeLabel || feedbackT('attribute'),
                  feedbackT('feedback_summary_n', { defaultValue: 'n' }),
                  feedbackT('min'),
                  feedbackT('q1'),
                  feedbackT('median'),
                  feedbackT('q3'),
                  feedbackT('max'),
                ]).map((heading) => (
                  <th key={heading} scope="col" style={cellStyle}>{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {summaryRows.map(({ key, attribute, n, stats, provenanceBreakdown }) => (
                <tr key={key}>
                  {!hideAttributeColumn ? (
                    <th scope="row" style={cellStyle}>
                      <span>{attribute}</span>
                      {provenanceBreakdown ? (
                        <span className="small" style={{ display: 'block', fontWeight: 400 }}>
                          {provenanceBreakdown}
                        </span>
                      ) : null}
                    </th>
                  ) : null}
                  {(compact ? [
                    n,
                    `${displayOneDecimal(stats.mean)} (${displayOneDecimal(stats.sd)})`,
                    `${displayUsefulPrecision(stats.median)} (${displayUsefulPrecision(stats.q1)}–${displayUsefulPrecision(stats.q3)})`,
                  ] : [n, stats.min, stats.q1, stats.median, stats.q3, stats.max]).map((value, index) => (
                    <td key={`${key}-${index}`} style={cellStyle}>{value}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      {compactPopulationLowCount && summaryRows.length === 0 && insufficientRows.length > 0 ? (
        <div
          data-testid="feedback-population-low-count"
          className="feedback-numeric-summary__low-count small"
        >
          {populationLowCountMessage}
        </div>
      ) : null}

      {(compactPopulationLowCount ? [] : insufficientRows).map(({ key, attribute, n, provenanceBreakdown }) => (
        <div
          key={key}
          data-testid={externalPopulationLabel ? 'feedback-low-count' : undefined}
          className={externalPopulationLabel
            ? 'feedback-numeric-summary__low-count small'
            : 'feedback-numeric-summary__state small'}
          style={externalPopulationLabel
            ? undefined
            : { alignItems: 'baseline', display: 'flex', flexWrap: 'wrap', gap: '0.25rem 0.5rem' }}
        >
          {!externalPopulationLabel ? (
            <strong>{caption}{attributeKeys.length > 1 ? ` — ${attribute}` : ''}</strong>
          ) : null}
          {compact ? (
            <>
              {showLowCountAttributeLabel ? <strong>{attribute}: </strong> : null}
              <span>
                {lowCountFormatter
                  ? lowCountFormatter(n)
                  : n === 1
                    ? lowCountSingular
                    : lowCountPlural?.replace('{{count}}', String(n))}
              </span>
              {lowCountHelp}
            </>
          ) : (
            <span>
              {feedbackT('feedback_summary_n', { defaultValue: 'n' })}={n}
              {' · '}
              {feedbackT(thresholdKey, {
                minimumN,
                defaultValue: thresholdDefault.replace('{{minimumN}}', String(minimumN)),
              })}
            </span>
          )}
          {provenanceBreakdown ? <span>{provenanceBreakdown}</span> : null}
        </div>
      ))}
    </div>
  );
}

function LegacyStatistics({
  userData,
  assessments,
  sessionEvaluations,
  consensusEvaluation,
  attributeKeys,
  feedbackT,
  translateLabel,
  attributeLabel,
}) {
  const observations = [
    ...(userData ? [userData] : []),
    ...assessments,
    ...sessionEvaluations,
    ...(consensusEvaluation ? [consensusEvaluation] : []),
  ];
  const rows = attributeKeys.map((key) => {
    const attributes = observations
      .map((observation) => observation?.perceptualData?.[key])
      .filter((value) => value && typeof value === 'object');
    const values = attributes.map((value) => value.VASValue).filter(Number.isFinite);
    return {
      key,
      attribute: getAttributeLabel(key, observations, translateLabel),
      present: attributes.filter((value) => value.isPresent === true).length,
      consistent: attributes.filter((value) => value.isConsistent === true).length,
      intermittent: attributes.filter((value) => value.isConsistent === false).length,
      stats: computeFiveNumberSummary(values) || {
        min: '-', q1: '-', median: '-', q3: '-', max: '-',
      },
    };
  });
  const totalCount = rows.reduce(
    (sum, row) => sum + row.present + row.consistent + row.intermittent,
    0
  );

  if (totalCount === 0) {
    return <p className="small" style={{ textAlign: 'center' }}>{feedbackT('no_statistical_data_available')}</p>;
  }

  const headings = [
    attributeLabel || feedbackT('attribute'),
    feedbackT('present'),
    feedbackT('consistent'),
    feedbackT('intermittent'),
    feedbackT('min'),
    feedbackT('q1'),
    feedbackT('median'),
    feedbackT('q3'),
    feedbackT('max'),
  ];

  return (
    <table style={tableStyle}>
      <thead><tr>{headings.map((heading) => <th key={heading} scope="col" style={cellStyle}>{heading}</th>)}</tr></thead>
      <tbody>
        {rows.map(({ key, attribute, present, consistent, intermittent, stats }) => (
          <tr key={key}>
            <th scope="row" style={cellStyle}>{attribute}</th>
            {[present, consistent, intermittent, stats.min, stats.q1, stats.median, stats.q3, stats.max]
              .map((value, index) => <td key={`${key}-${index}`} style={cellStyle}>{value}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const AttributeStatsTable = ({
  mode,
  observations = [],
  minimumN = 5,
  populationKind = 'peers',
  summaryVariant = 'five-number',
  caption = '',
  summaryHelp = null,
  lowCountHelp = null,
  lowCountSingular = '1 rating available for this sample.',
  lowCountPlural = '{{count}} ratings available for this sample.',
  lowCountFormatter = null,
  externalPopulationLabel = false,
  hideAttributeColumn = false,
  showLowCountAttributeLabel = false,
  compactPopulationLowCount = false,
  populationLowCountMessage = null,
  userData = null,
  assessments = [],
  sessionEvaluations = [],
  consensusEvaluation = null,
  attributeKeys = [],
  labelT = null,
  attributeLabel = null,
  provenanceBreakdownByKey = null,
}) => {
  const { t } = useTranslation();
  const feedbackT = labelT || t;

  if (mode === 'legacy') {
    return (
      <LegacyStatistics
        userData={userData}
        assessments={assessments}
        sessionEvaluations={sessionEvaluations}
        consensusEvaluation={consensusEvaluation}
        attributeKeys={attributeKeys}
        feedbackT={feedbackT}
        translateLabel={feedbackT}
        attributeLabel={attributeLabel}
      />
    );
  }

  if (mode !== 'numeric-summary') return null;

  return (
    <GovernedNumericSummary
      observations={observations}
      attributeKeys={attributeKeys}
      minimumN={minimumN}
      populationKind={populationKind}
      summaryVariant={summaryVariant}
      caption={caption}
      summaryHelp={summaryHelp}
      lowCountHelp={lowCountHelp}
      lowCountSingular={lowCountSingular}
      lowCountPlural={lowCountPlural}
      lowCountFormatter={lowCountFormatter}
      externalPopulationLabel={externalPopulationLabel}
      hideAttributeColumn={hideAttributeColumn}
      showLowCountAttributeLabel={showLowCountAttributeLabel}
      compactPopulationLowCount={compactPopulationLowCount}
      populationLowCountMessage={populationLowCountMessage}
      feedbackT={feedbackT}
      translateLabel={feedbackT}
      attributeLabel={attributeLabel}
      provenanceBreakdownByKey={provenanceBreakdownByKey}
    />
  );
};

export default AttributeStatsTable;
